import { ConfirmationModal, ExtendedFAB, Icon, LoadingInner, styleClassNames, tableClassNames, TableInner, TConfirmationModalProps, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { v4 as uuidv4 } from 'uuid';

import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ONE_OFF_EVENT_TYPES, translateEventType } from '../../entities/events/shared';
import { TEventTypes, useCreateEventGiftMutation, useDeleteEventMutation, useSkipEventMutation } from '../../generated/graphql';

import { CountryName } from '../../components/coutryName/CountryName';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { TEventType, useOneOffUpcomingEventsNEW } from '../../entities/events/queries';
import { calculatedShippingDate } from '../../entities/gifts/shared';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import { getSubRows, useEventsFilters } from './_shared';

export const OneOffEventsNEW = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const tableRef = createRef<TTablePageRef>();

  const [includingNextDays, setIncludingNextDays] = useState<number>(60);

  const { data, error, loading, nextToken, fetchMore } = useOneOffUpcomingEventsNEW({ includingNextDays });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading,
    queries: [
      { fetchMore: fetchMore.babyFetchMore, nextToken: nextToken.babyNextToken },
      { fetchMore: fetchMore.bereavementFetchMore, nextToken: nextToken.bereavementNextToken },
      { fetchMore: fetchMore.weddingFetchMore, nextToken: nextToken.weddingNextToken },
      { fetchMore: fetchMore.replacementWAFetchMore, nextToken: nextToken.replacementWANextToken },
      { fetchMore: fetchMore.replacementEBFetchMore, nextToken: nextToken.replacementEBNextToken },
      { fetchMore: fetchMore.careFetchMore, nextToken: nextToken.careNextToken },
      { fetchMore: fetchMore.customBricksFetchMore, nextToken: nextToken.customBricksNextToken },
      { fetchMore: fetchMore.otherFetchMore, nextToken: nextToken.otherNextToken }
    ]
  });

  const hasMorePages = Object.values(nextToken).some(Boolean);

  const events = useMemo(() => getSubRows(data), [data]);

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    offices,
    officeFilter,
    filterByOffice,
    setOffice,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    filterByRecipientName,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  } = useEventsFilters({ events });

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [skipLoading, setSkipLoading] = useState<boolean>(false);
  const [createGiftsLoading, setCreateGiftsLoading] = useState<boolean>(false);

  const [deletedAllLoading, setDeletedAllLoading] = useState<boolean>(false);
  const [deleteConfirmationProps, setDeleteConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();

  const [skipEvent] = useSkipEventMutation();
  const [createEventGift] = useCreateEventGiftMutation();

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      return filterByCompany(e) && filterByOffice(e) && filterByMonth(e) && filterByEventType(e) && filterByClientManager(e) && filterByRecipientName(e);
    });
  }, [filterByCompany, filterByMonth, filterByOffice, events, filterByEventType, filterByClientManager, filterByRecipientName]);

  //console.log('flatEvents NEW?:', data);
  //console.log('events NEW?:', events);
  console.log('filteredEvents NEW?:', filteredEvents);

  const skipEvents = useCallback(async () => {
    setSkipLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const event = events.find((item) => item.id === id);
        if (!event) continue;
        recordId = event.id;
        await skipEvent({
          variables: { id: event.id, companyId: event.companyId, version: event._version }
        });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows([]);
    } catch (error) {
      console.error('Event skip failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error skipping the selected events.');
    } finally {
      setSkipLoading(false);
    }
  }, [events, selectedRows, skipEvent, tableRef]);

  const [deleteEvent] = useDeleteEventMutation();

  const onDeletedAllClick = useCallback(async () => {
    setDeletedAllLoading(true);
    let recordId: string | undefined;
    try {
      const deletedIds = new Set<string>();
      for (const id of selectedRows) {
        const event = filteredEvents.find((item) => item.id === id);
        if (!event) continue;
        for (const target of [...(event.subRows || []), event]) {
          if (deletedIds.has(target.id)) continue;
          recordId = target.id;
          await deleteEvent({ variables: { companyId: target.companyId, id: target.id, version: target._version } });
          deletedIds.add(target.id);
          toast.success(`Event for ${target.recipient?.fullName} has been deleted successfully.`);
        }
      }
      setDeleteConfirmationProps(undefined);
    } catch (error) {
      console.error('Event deletion failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error trying to delete the selected events.');
    } finally {
      setDeletedAllLoading(false);
    }
  }, [selectedRows, deleteEvent, filteredEvents]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationProps({
      headline: 'Remove Events',
      supportingText: `You are going to remove ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}. Are you sure?`,
      confirmLabel: 'Remove',
      onConfirmClick: () => onDeletedAllClick()
    });
  }, [onDeletedAllClick, setDeleteConfirmationProps, selectedRows]);

  const createGifts = useCallback(async () => {
    setCreateGiftsLoading(true);
    let recordId: string | undefined;
    try {
      const createdIds = new Set<string>();
      const selectedEvents = (selectedRows || []).map((id) => events.find((event) => event.id === id)).filter((event) => event != null);
      const groupedEvents = _.groupBy(selectedEvents, (event) => event.employee?.officeId ?? event.addressId);
      for (const groupEvents of Object.values(groupedEvents)) {
        const officeGroup = uuidv4();
        for (const event of groupEvents) {
          const group = event.subRows?.length ? event.id : officeGroup;
          for (const target of [...(event.subRows || []), event]) {
            if (createdIds.has(target.id)) continue;
            recordId = target.id;
            await createEventGift({
              variables: {
                eventId: target.id,
                companyId: target.companyId,
                eventVersion: target._version,
                group,
                shippingDate: calculatedShippingDate({
                  date: target.eventDate,
                  address: {
                    country: target.address?.country as string,
                    state: target.address?.state as string
                  }
                })
              }
            });
            createdIds.add(target.id);
          }
        }
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows([]);
    } catch (error) {
      console.error('Event gift creation failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error creating the selected gifts.');
    } finally {
      setCreateGiftsLoading(false);
    }
  }, [createEventGift, events, selectedRows, tableRef]);

  const onSkipEventsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Skip ${selectedRows.length} Events?`,
      supportingText: `Are you sure you want to skip these ${selectedRows.length} events?`,
      confirmLabel: 'Skip',
      onConfirmClick: skipEvents,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, skipEvents, tableRef, tableRef.current]);

  const onCreateGiftsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Create ${selectedRows.length} Gifts?`,
      supportingText: `Are you sure you want to create these ${selectedRows.length} gifts?`,
      confirmLabel: 'Create',
      onConfirmClick: createGifts,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGifts, selectedRows, tableRef, tableRef.current]);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((event: TEventType) => {
    return event.id;
  }, []);

  const getRowClassName = useCallback((row: Record<string, any>) => {
    if (!row.original.address || !row.original.recipient) return styleClassNames.attentionRow;
  }, []);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Date',
      name: 'date',
      onChange: setMonth,
      options: months.map((m) => ({ label: m, value: m })),
      value: monthFilter
    });

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: setCompany,
      value: companyFilter,
      options: companies?.map((c) => ({
        label: `${c.name} (${companiesCounts[c.id]})`,
        value: c.id
      }))
    });

    if (companyFilter && offices.length > 0)
      filtersList.push({
        type: 'singleSelect',
        label: 'Office',
        name: 'office',
        onChange: setOffice,
        options: offices.map((o) => ({ label: o.name, value: o.id })),
        value: officeFilter
      });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient Name',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientNameFilter(v != null ? (v as string) : undefined);
      },
      value: recipientNameFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Event Type',
      name: 'eventType',
      onChange: (v) => {
        setEventTypeFilter(v != null ? (v as TEventTypes) : undefined);
      },
      options: Object.entries(ONE_OFF_EVENT_TYPES).map(([key, value]) => ({ label: value, value: key })),
      value: eventTypeFilter
    });

    filtersList.push({
      includeEmptyOption: false,
      type: 'singleSelect',
      label: 'Including Next Days',
      name: 'includingNextDays',
      onChange: (v) => {
        setIncludingNextDays(v != null ? Number(v) : 60);
      },
      options: [
        {
          label: '60 Days',
          value: 60
        },
        {
          label: '90 Days',
          value: 90
        },
        {
          label: 'All Days',
          value: 999
        }
      ],
      value: includingNextDays
    });

    return filtersList;
  }, [
    companies,
    companiesCounts,
    companyFilter,
    monthFilter,
    months,
    officeFilter,
    offices,
    setCompany,
    setMonth,
    setOffice,
    eventTypeFilter,
    setEventTypeFilter,
    includingNextDays,
    recipientNameFilter,
    setRecipientNameFilter,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter
  ]);

  return (
    <TablePage actionsLoading={createGiftsLoading || skipLoading} error={error} ref={tableRef} title='Upcoming One-Off Events (NEW)'>
      {!_.isEmpty(deleteConfirmationProps) && (
        <ConfirmationModal {...deleteConfirmationProps} closeModal={() => setDeleteConfirmationProps(undefined)} confirmLoading={deletedAllLoading} onCancelClick={() => setDeleteConfirmationProps(undefined)} />
      )}
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TEventType>
          enableSorting={true}
          defaultSorting={{ id: 'shippingDate', desc: false }}
          data={filteredEvents}
          emptyText={events.length === 0 ? 'No upcoming one-off events.' : 'No upcoming one-off events for the current filters.'}
          expandable={true}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          hideSubRowsSelect={true}
          hoverableRows={!skipLoading && !createGiftsLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.wasPaused && (
                      <Tooltip message='This gift was paused.'>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.company?.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName',
              cell: ({ cell }) => {
                let text = '';
                const pausePeriod = cell.row.original.employee?.pausePeriod;
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.recipient?.fullName}</span>
                  </div>
                );
              }
            },
            {
              header: 'Shipping Date',
              accessorKey: 'shippingDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.shippingDate ?? undefined)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{cell.row.original.type === TEventTypes.Other ? cell.row.original.customType : translateEventType(cell.row.original.type)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.eventDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Office',
              accessorKey: 'employee.office.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{cell.row.original.employee?.office?.name || '-'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Country',
              accessorKey: 'address.country',
              cell: ({ cell }) => {
                if (isAddressValid(cell.row.original.address))
                  return (
                    <div className={clsx(tableClassNames.primary)}>
                      <CountryName name={cell.row.original.address?.country || undefined} />
                    </div>
                  );
                else
                  return (
                    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                      <Icon icon='warning' />
                      <span> data</span>
                    </div>
                  );
              }
            }
          ]}
        />
        {loading && <LoadingInner className={clsx(tableStyles.contentLoader)} />}
        {hasMorePages && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB
            // disabled={printAllLoading}
            label={`Delete ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}`}
            leadingIcon='delete'
            loading={deletedAllLoading}
            onClick={onDeleteClick}
            variant='tertiary'
          />
          <ExtendedFAB disabled={createGiftsLoading} label={`Skip ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='exit_to_app' loading={skipLoading} onClick={onSkipEventsClick} variant='secondary' />
          <ExtendedFAB disabled={skipLoading} label={`Create ${selectedRows.length} gift${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='add' loading={createGiftsLoading} onClick={onCreateGiftsClick} variant='surface' />
        </FloatingActions>
      )}
    </TablePage>
  );
};
